import type { Panel } from '../state';

// Voice search with the browser's own speech recognition (Chrome, Edge and Safari; Safari may run it
// on the device). Nothing is recorded or kept; if the browser has no recognition, the mic is hidden.

type Rec = {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  continuous: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
};

const Ctor = (): (new () => Rec) | null => {
  const w = window as unknown as { SpeechRecognition?: new () => Rec; webkitSpeechRecognition?: new () => Rec };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};
export const voiceSupported = () => !!Ctor();

const ERRORS: Record<string, string> = {
  'not-allowed': 'Microphone access was declined. You can allow it in your browser settings.',
  'service-not-allowed': 'Voice search is not available in this browser.',
  'no-speech': 'I did not catch that. Tap the microphone and try again.',
  network: 'Voice search needs a connection in this browser.',
  'audio-capture': 'No microphone was found.',
};

// Chrome can recognise speech on the device (no audio leaves it) once its language pack is installed;
// when it can, it also accepts a list of names to listen out for. Checked once, ahead of the tap.
let onDevice = false;
export async function prepareVoice(phrases: string[]) {
  const C = Ctor() as unknown as { available?: (o: { langs: string[]; processLocally: boolean }) => Promise<string> } | null;
  if (!C?.available) return;
  try {
    onDevice = (await C.available({ langs: ['en-GB'], processLocally: true })) === 'available';
    hints = phrases.slice(0, 400);
  } catch {
    onDevice = false;
  }
}
let hints: string[] = [];
export const voiceOnDevice = () => onDevice;

/**
 * Listen once; returns a function that stops listening. Final results come with the recogniser's
 * alternatives, so the caller can pick the one that makes sense. A watchdog gives up if the browser
 * goes quiet (Safari can hang after audio has played).
 */
export function listen(h: { onText: (text: string, final: boolean, alternatives: string[]) => void; onError: (message: string) => void; onEnd: () => void }): () => void {
  const C = Ctor();
  if (!C) {
    h.onError('Voice search is not available in this browser.');
    h.onEnd();
    return () => {};
  }
  const r = new C() as Rec & { processLocally?: boolean; phrases?: unknown[] };
  r.lang = 'en-GB';
  r.interimResults = true;
  r.maxAlternatives = 4;
  r.continuous = false;
  if (onDevice) {
    r.processLocally = true;
    const P = (window as unknown as { SpeechRecognitionPhrase?: new (p: string, boost: number) => unknown }).SpeechRecognitionPhrase;
    if (P && 'phrases' in r) r.phrases = hints.map((x) => new P(x, 2));
  }
  let ended = false, heard = false;
  let dog: ReturnType<typeof setTimeout> | undefined;
  const finish = () => {
    if (ended) return;
    ended = true;
    clearTimeout(dog);
    h.onEnd();
  };
  const arm = (ms: number) => {
    clearTimeout(dog);
    dog = setTimeout(() => {
      try {
        r.abort();
      } catch {
        /* gone */
      }
      // After a final result the browser has simply not said it is done: end quietly.
      if (!heard) h.onError('Voice search stopped responding. Try again, or type instead.');
      finish();
    }, ms);
  };
  r.onresult = (e) => {
    let text = '', final = false;
    const alts: string[] = [];
    for (let i = 0; i < e.results.length; i++) {
      text += e.results[i][0].transcript;
      final = e.results[i].isFinal;
    }
    if (final) {
      const last = e.results[e.results.length - 1];
      const head = text.slice(0, text.length - last[0].transcript.length);
      for (let k = 0; k < last.length; k++) alts.push((head + last[k].transcript).trim());
    }
    if (final) heard = true;
    arm(final ? 3000 : 6000);
    h.onText(text.trim(), final, alts);
  };
  r.onerror = (e) => {
    if (e.error !== 'aborted') h.onError(ERRORS[e.error] ?? 'Voice search stopped. Try again.');
  };
  r.onend = finish;
  try {
    r.start();
    arm(9000);
  } catch {
    h.onError('Voice search could not start.');
    finish();
  }
  return () => {
    try {
      r.stop();
    } catch {
      /* already stopped */
    }
  };
}

export type Band = 'low' | 'high' | 'overhead' | null;
export type VoiceIntent =
  | { kind: 'panel'; panel: Panel }
  | { kind: 'point' }
  | { kind: 'now' }
  | { kind: 'face'; az: number }
  | { kind: 'bright'; az: number | null; band: Band; what: 'star' | 'planet' | 'any'; colour: 'red' | null }
  | { kind: 'search'; query: string };

const COMPASS: Record<string, number> = {
  north: 0, 'north east': 45, northeast: 45, east: 90, 'south east': 135, southeast: 135,
  south: 180, 'south west': 225, southwest: 225, west: 270, 'north west': 315, northwest: 315,
};
const DIR = '(north ?east|north ?west|south ?east|south ?west|north|south|east|west)';

const UNITS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
const NUMBER: Record<string, number> = Object.fromEntries([...UNITS.map((w, i) => [w, i]), ...TENS.slice(2).map((w, i) => [w, (i + 2) * 10])]);
/** "m thirty one" → "m 31", "ngc seven thousand eight hundred and forty" → "ngc 7840". */
function numbers(t: string): string {
  const w = t.replace(/(\w)-(\w)/g, '$1 $2').split(' ');
  const out: string[] = [];
  for (let i = 0; i < w.length; ) {
    let j = i, total = 0, cur = 0, any = false;
    for (; j < w.length; j++) {
      const x = w[j], next = w[j + 1];
      if (x in NUMBER) (cur += NUMBER[x]), (any = true);
      else if (x === 'hundred' && any) cur = (cur || 1) * 100;
      else if (x === 'thousand' && any) (total += (cur || 1) * 1000), (cur = 0);
      else if (x === 'a' && (next === 'hundred' || next === 'thousand')) (cur = 1), (any = true);
      else if (x === 'and' && any && next !== undefined && next in NUMBER) continue;
      else break;
    }
    if (any) {
      out.push(String(total + cur));
      i = j;
    } else out.push(w[i++]);
  }
  return out.join(' ');
}

// What speech recognisers commonly hear instead of sky names.
const MISHEARD: [RegExp, string][] = [
  [/\bbeetle ?juice\b/g, 'betelgeuse'],
  [/\bserious\b/g, 'sirius'],
  [/\bvegas\b/g, 'vega'],
  [/\bvenice\b/g, 'venus'],
  [/\b(i s s|i ss|eye ss|the space station|space station)\b/g, 'iss'],
  [/\bor ryan\b|\bo ryan\b/g, 'orion'],
  [/\bcassie oh pia\b/g, 'cassiopeia'],
  [/\bmessy a\b|\bmessiah\b/g, 'messier'],
  [/\bthe plow\b|\bplow\b/g, 'plough'],
  [/\b(\w+?)'?s (rings|moons)\b/g, '$1'],
];

/** Turn what was said into an action: open a part of the app, face a direction, or find something. */
export function parseVoice(said: string): VoiceIntent {
  let t = said.toLowerCase().replace(/[.,!?]/g, '').replace(/\s+/g, ' ').trim();
  for (const [re, to] of MISHEARD) t = t.replace(re, to);
  // Numbers only after catalogue names, so a name with a number word in it stays as it is.
  t = t.replace(/\b(m|messier|ngc|ic) ([a-z -]+)/g, (_, c, rest) => `${c} ${numbers(rest)}`);
  t = t.replace(/\b(m|messier|ngc|ic) (\d+)\b/g, (_, c, n) => (c === 'messier' ? `m${n}` : `${c}${n}`));

  // "What's that bright star in the west?", "the brightest thing low in the south", "that red one".
  const bright = /\b(what('?s| is) (that|this|the)|which is the|show me the)\b.*\b(bright(est)?|red(dish)?|shiny|light|star|planet)\b/.test(t);
  const dirM = new RegExp(`\\b(in|to(wards)?|over) the ${DIR}\\b|\\b${DIR}ern sky\\b|\\blow (in the )?${DIR}\\b`).exec(t);
  const dirWord = dirM ? (dirM[3] ?? dirM[4] ?? dirM[6])?.replace(/(north|south) ?(east|west)/, '$1 $2') : undefined;
  const band: Band = /\boverhead\b|\bstraight up\b/.test(t) ? 'overhead' : /\blow\b|\bnear the horizon\b/.test(t) ? 'low' : /\bhigh\b/.test(t) ? 'high' : null;
  if (bright && (dirWord || band || /\bbrightest\b/.test(t) || (/\bred(dish)?\b/.test(t) && !/\bred planet\b/.test(t)))) {
    const what = /\bplanet\b/.test(t) ? 'planet' : /\bstar\b/.test(t) ? 'star' : 'any';
    return { kind: 'bright', az: dirWord ? COMPASS[dirWord] : null, band, what, colour: /\bred(dish)?\b/.test(t) ? 'red' : null };
  }
  if (/\bwhat (am i|are we) (looking|pointing) at\b|^what('?s| is) (that|this)( bright one| star| light| one)?$/.test(t)) return { kind: 'point' };
  // Something seen moving: Tonight, which has the satellite passes and meteor showers.
  if (/\bwhat was that\b|\bwas that an?\b|\b(i|we) (just )?(saw|seen|spotted)\b|\bshooting star\b.*\b(was|saw)\b|\bmoving (light|star|thing)\b|\bflashing\b/.test(t)) return { kind: 'panel', panel: 'tonight' };
  const face = new RegExp(`\\b(which way is|where('?s| is)|face|turn to|look) ${DIR}( please| now| tonight| for\\b.*| to (see|find|spot)\\b.*)?$`).exec(t);
  if (face) return { kind: 'face', az: COMPASS[face[3].replace(/(north|south) ?(east|west)/, '$1 $2')] };
  if (/\bwhere (should|can|could) (i|we) go\b|\bdark(er)? (sky|skies|site|place)s?\b/.test(t)) return { kind: 'panel', panel: 'go' };
  if (/\b(sky )?hunt\b|\bplay a game\b/.test(t)) return { kind: 'panel', panel: 'hunt' };
  if (/\b(guided )?tour\b/.test(t)) return { kind: 'panel', panel: 'tour' };
  if (/\bquiz( me)?\b|\btest me\b/.test(t)) return { kind: 'panel', panel: 'quiz' };
  const about = /^(?:(?:can you |please )?teach me|learn|tell me) about (?:the )?(.+)$/.exec(t);
  const topic = /^(stars?|constellations?|planets?|galaxies|nebulae|space|astronomy|(night )?sky|star colou?rs|dark skies|shooting stars?|meteors?)$/;
  if (about && !topic.test(about[1])) return { kind: 'search', query: about[1] };
  if (/\b(teach me|lessons?|learn)\b/.test(t)) return { kind: 'panel', panel: 'learn' };
  if (/\b(what('?s| is) up|what can (i|we) see)( tonight)?\b|^tonight$|\bweather\b|\bclear tonight\b|\bcloudy\b/.test(t)) return { kind: 'panel', panel: 'tonight' };
  if (/^(back to )?now$|\bgo back to now\b/.test(t)) return { kind: 'now' };
  const query = t
    .replace(/^(ok(ay)? )?(ahillah,? )?/, '')
    .replace(/^(please )?(show me|where('?s| is| are)|find|look for|search for|take me to|point (me )?(to|at)|go to|tell me about|what('?s| is)|when (does|do|is|can i see|will)( i see)?|(can|could|will) (i|we|you) see|is|are)( the)? /, '')
    .replace(/\b(next )?(pass over|passes|pass|come over|rises|rise|sets|set|be visible|visible|come up|go down)\b/g, '')
    .replace(/\b(please|right now|tonight|tomorrow|in the sky|over(head)? here)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return { kind: 'search', query };
}

export interface Candidate<T> { item: T; alt: number; az: number; mag: number; type: 'star' | 'planet' | 'moon' | 'satellite'; bv?: number }

/** The object someone probably means by "that bright star in the west". */
export function brightestMatch<T>(cands: Candidate<T>[], it: Extract<VoiceIntent, { kind: 'bright' }>): Candidate<T> | null {
  const angle = (a: number, b: number) => Math.abs(((a - b + 540) % 360) - 180);
  const ok = cands.filter((c) => {
    if (c.alt < 3) return false;
    if (it.az != null && angle(c.az, it.az) > 50) return false;
    if (it.band === 'low' && c.alt > 30) return false;
    if (it.band === 'high' && c.alt < 40) return false;
    if (it.band === 'overhead' && c.alt < 60) return false;
    // People call planets stars, so "star" allows planets; "planet" does not allow stars.
    if (it.what === 'planet' && c.type !== 'planet') return false;
    if (it.what === 'star' && c.type !== 'star' && c.type !== 'planet') return false;
    if (it.colour === 'red' && !(c.bv != null ? c.bv > 1.2 : false)) return false;
    return c.type !== 'satellite';
  });
  return ok.sort((a, b) => a.mag - b.mag)[0] ?? null;
}
